/**
 * Super-admin member directory and cross-facility access editor.
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FirestoreService } from "../firebase/firestoreService";
import { FirestoreCollectionReference } from "../firebase/references";
import {
  bulkUpdateMember,
  deleteMember,
  getMemberErrorMessage,
  resetMemberTwoFactor,
} from "../services/memberManagementService";
import type { MemberManagementError } from "../types";
import CustomTable from "../components/custom-table/custom-table";
import { LoaderMessage } from "../components/loaderMessage/loaderMessage";
import DeleteUserModal from "../components/modals/deleteUserModal/deleteUserModal";
import ResetTwoFactorModal from "../components/modals/resetTwoFactorModal/resetTwoFactorModal";
import UserAccessSidePanel, {
  FacilityWithId,
  UserAccessData,
} from "../components/modals/userAccessSidePanel/userAccessSidePanel";
import { ToastMessages } from "../components/toasts/toastMessages";
import { RbacRoleId } from "../types";
import { SuperAdminTableColumnKeys } from "../enums/tableColumnKeys";
import { fetchAllProvidersForSuperAdmin } from "../state/superAdminProvidersSlice";
import type {
  SuperAdminProvider,
  FacilityInfo,
  DataState,
  ProviderWithId,
} from "../types";
import { useAuthSelector, useAuthDispatch } from "../state/AuthStoreProvider";
import type { AuthRootState } from "../state/store";
import {
  generateUIDataForSuperAdminTable,
  SuperAdminMember,
  SuperAdminTableHeaderColumns,
} from "./superAdminData";
import { getDefaultFacilityRole } from "./facilityRoleDefaults";

function convertProviderToMember(
  provider: SuperAdminProvider,
  facilityMap: Map<string, FacilityInfo>,
): SuperAdminMember {
  let internalFacilityIds = provider.internal_healthcare_facilities || [];
  let externalFacilityIds = provider.external_healthcare_facilities || [];

  if (
    internalFacilityIds.length === 0 &&
    externalFacilityIds.length === 0 &&
    provider.healthcare_facilities?.length
  ) {
    provider.healthcare_facilities.forEach((facilityId) => {
      const facilityInfo = facilityMap.get(facilityId);
      if (facilityInfo?.isExternal) {
        externalFacilityIds = [...externalFacilityIds, facilityId];
      } else {
        internalFacilityIds = [...internalFacilityIds, facilityId];
      }
    });
  }

  const internalOrgs = internalFacilityIds
    .map((id) => facilityMap.get(id)?.name || id)
    .filter(Boolean);
  const externalOrgs = externalFacilityIds
    .map((id) => facilityMap.get(id)?.name || id)
    .filter(Boolean);

  const providerId = provider.uid || provider.id;

  let role: "Super Admin" | "Admin" | "Member" = "Member";
  if (provider.role_id === RbacRoleId.SUPER_ADMIN) {
    role = "Super Admin";
  } else if (
    provider.role_id === RbacRoleId.ADMIN_MED_ONC ||
    provider.role_id === RbacRoleId.ADMIN_PHARMA ||
    provider.IsAdmin
  ) {
    role = "Admin";
  }

  return {
    id: providerId,
    name:
      `${provider.FirstName || ""} ${provider.LastName || ""}`.trim() ||
      "Unknown",
    email: provider.EmailAddresses?.[0] || "",
    role,
    internalOrgIds: internalFacilityIds,
    externalOrgIds: externalFacilityIds,
    internalOrgs,
    externalOrgs,
    status: provider.Status || "",
  };
}

/**
 * Renders the global member directory and facility access editor.
 *
 * @returns Super-admin settings page
 */
const SuperAdminPage = () => {
  const dispatch = useAuthDispatch();

  const { data: singleProviderDetails } = useAuthSelector(
    (state: AuthRootState) => state.providerDetails,
  ) as DataState<ProviderWithId>;

  const superAdminState = useAuthSelector(
    (state: AuthRootState) =>
      (state as any).superAdminProviders as {
        providers: SuperAdminProvider[];
        facilities: FacilityInfo[];
        loading: boolean;
        error: string | null;
      },
  );

  const {
    providers,
    facilities,
    loading: isLoading,
    error: loadError,
  } = superAdminState;

  const [tableData, setTableData] = useState<any[]>([]);
  const [filteredTableData, setFilteredTableData] = useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [isAccessPanelOpen, setIsAccessPanelOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<SuperAdminMember | null>(
    null,
  );
  const [memberToReset, setMemberToReset] = useState<SuperAdminMember | null>(
    null,
  );
  const [memberToDelete, setMemberToDelete] = useState<SuperAdminMember | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);

  const facilityMap = useMemo(() => {
    const map = new Map<string, FacilityInfo>();
    facilities.forEach((f) => map.set(f.id, f));
    return map;
  }, [facilities]);

  const membersData = useMemo(() => {
    return providers
      .map((p) => convertProviderToMember(p, facilityMap))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [providers, facilityMap]);

  const internalFacilities = useMemo(
    () => facilities.filter((f) => !f.isExternal),
    [facilities],
  );
  const externalFacilities = useMemo(
    () => facilities.filter((f) => f.isExternal),
    [facilities],
  );

  const handleToggleActive = useCallback(
    async (memberId: string, isActive: boolean) => {
      try {
        const newStatus = isActive ? "active" : "disabled";
        const collectionPath = FirestoreCollectionReference.providers();
        const cleanPath = collectionPath.replace(/^\//, "").replace(/\/$/, "");

        await FirestoreService.updateDocument(cleanPath, memberId, {
          Status: newStatus,
        });

        toast.success(ToastMessages.USER_STATUS_UPDATE_SUCCESS);
      } catch (error) {
        console.error("Failed to update user status:", error);
        toast.error(ToastMessages.USER_STATUS_UPDATE_FAILED);
      }
    },
    [],
  );

  const handleResetTwoFactor = useCallback((member: SuperAdminMember) => {
    setMemberToReset(member);
  }, []);

  const handleResetTwoFactorClose = useCallback(() => {
    setMemberToReset(null);
  }, []);

  const handleResetTwoFactorConfirm = useCallback(async () => {
    if (!memberToReset) {
      return;
    }
    try {
      const facilityId = singleProviderDetails?.FacilityId || "";
      if (!facilityId) {
        throw new Error("No facility ID available");
      }
      await resetMemberTwoFactor(facilityId, memberToReset.id);
      toast.success(ToastMessages.USER_2FA_RESET_SUCCESS);
    } catch (error) {
      const apiError = error as MemberManagementError;
      console.error(
        "Failed to reset 2FA:",
        apiError.message || getMemberErrorMessage(apiError),
      );
      toast.error(ToastMessages.USER_2FA_RESET_FAILED);
    }
  }, [memberToReset, singleProviderDetails?.FacilityId]);

  const handleDeleteMember = useCallback((member: SuperAdminMember) => {
    setMemberToDelete(member);
  }, []);

  const handleDeleteMemberClose = useCallback(() => {
    setMemberToDelete(null);
  }, []);

  const handleDeleteMemberConfirm = useCallback(async (memberId: string) => {
    if (!memberId) {
      return;
    }
    try {
      const facilityId = singleProviderDetails?.FacilityId || "";
      if (!facilityId) {
        throw new Error("No facility ID available");
      }
      await deleteMember(facilityId, memberId);
      toast.success(ToastMessages.USER_DELETED_SUCCESS);
    } catch (error) {
      const apiError = error as MemberManagementError;
      console.error(
        "Failed to delete member:",
        apiError.message || getMemberErrorMessage(apiError),
      );
      toast.error(ToastMessages.USER_DELETE_FAILED);
    }
  }, [singleProviderDetails?.FacilityId]);

  useEffect(() => {
    const unsubscribePromise = dispatch(
      fetchAllProvidersForSuperAdmin() as any,
    );

    return () => {
      if (
        unsubscribePromise &&
        typeof (unsubscribePromise as any).then === "function"
      ) {
        (unsubscribePromise as any).then((unsubscribe: any) => {
          if (typeof unsubscribe === "function") {
            unsubscribe();
          }
        });
      }
    };
  }, [dispatch]);

  useEffect(() => {
    setTableData(
      generateUIDataForSuperAdminTable(membersData, {
        onEditMember: (member) => {
          setSelectedMember(member);
          setIsAccessPanelOpen(true);
        },
        onDeleteMember: handleDeleteMember,
        onToggleActive: handleToggleActive,
        onResetTwoFactor: handleResetTwoFactor,
      }),
    );
  }, [membersData, handleToggleActive, handleResetTwoFactor, handleDeleteMember]);

  useEffect(() => {
    if (searchText.trim() === "") {
      setFilteredTableData(tableData);
    } else {
      const lowerSearchText = searchText.toLowerCase();
      const filtered = tableData.filter((row: any) => {
        const name = (
          row[SuperAdminTableColumnKeys.Member] || ""
        ).toLowerCase();
        const email = (row[SuperAdminTableColumnKeys.Mail] || "").toLowerCase();
        return (
          name.includes(lowerSearchText) || email.includes(lowerSearchText)
        );
      });
      setFilteredTableData(filtered);
    }
  }, [tableData, searchText]);

  const handleAccessPanelClose = useCallback(() => {
    setIsAccessPanelOpen(false);
    setSelectedMember(null);
  }, []);

  const handleAccessPanelSave = useCallback(
    async (data: UserAccessData) => {
      setIsSaving(true);
      try {
        const facilityId = singleProviderDetails?.FacilityId || "";
        if (!facilityId) {
          throw new Error("No facility ID available");
        }

        const originalFacilityIds = new Set([
          ...(selectedMember?.internalOrgIds || []),
          ...(selectedMember?.externalOrgIds || []),
        ]);

        /**
         * Preserves a member's existing facility role while rebuilding access.
         *
         * @param facId - Facility whose assignment should be read
         * @returns Existing assignable role, or the standard medical role when absent
         */
        const getFacilityRole = async (
          facId: string,
        ): Promise<
          | typeof RbacRoleId.ADMIN_MED_ONC
          | typeof RbacRoleId.STANDARD_MED_ONC
          | typeof RbacRoleId.ADMIN_PHARMA
          | typeof RbacRoleId.STANDARD_PHARMA
          | typeof RbacRoleId.CLINICAL_TRIALS_USER
        > => {
          if (!originalFacilityIds.has(facId)) {
            return getDefaultFacilityRole(facilityMap.get(facId));
          }
          try {
            const path = `${FirestoreCollectionReference.healthFacilityProviders(facId)}${data.memberId}`;
            const doc = await FirestoreService.getDocument<{
              role_id?: string;
            }>(path);
            if (doc?.role_id === RbacRoleId.ADMIN_MED_ONC) {
              return RbacRoleId.ADMIN_MED_ONC;
            }
            if (doc?.role_id === RbacRoleId.ADMIN_PHARMA) {
              return RbacRoleId.ADMIN_PHARMA;
            }
            if (doc?.role_id === RbacRoleId.STANDARD_PHARMA) {
              return RbacRoleId.STANDARD_PHARMA;
            }
            if (doc?.role_id === RbacRoleId.CLINICAL_TRIALS_USER) {
              return RbacRoleId.CLINICAL_TRIALS_USER;
            }
          } catch {}
          return getDefaultFacilityRole(facilityMap.get(facId));
        };

        const buildFacilityPayload = (facilityIds: string[]) =>
          Promise.all(
            facilityIds.map(async (id) => ({
              facilityId: id,
              roleId: await getFacilityRole(id),
            })),
          );

        const [intFacilities, extFacilities] = await Promise.all([
          buildFacilityPayload(data.internalOrgIds),
          buildFacilityPayload(data.externalOrgIds),
        ]);

        await bulkUpdateMember(facilityId, {
          userId: data.memberId,
          email: data.email,
          name: data.name,
          internalFacilities: intFacilities,
          externalFacilities: extFacilities,
        });

        toast.success(ToastMessages.USER_ACCESS_UPDATE_SUCCESS);
        setIsAccessPanelOpen(false);
        setSelectedMember(null);
      } catch (error) {
        const apiError = error as MemberManagementError;
        const errorMessage =
          apiError.message || getMemberErrorMessage(apiError);
        console.error("Failed to update user access:", errorMessage);
        toast.error(ToastMessages.USER_ACCESS_UPDATE_FAILED);
      } finally {
        setIsSaving(false);
      }
    },
    [singleProviderDetails?.FacilityId, selectedMember, facilityMap],
  );

  const handleSearch = (text: string) => {
    setSearchText(text);
  };

  const availableInternalOrgs: FacilityWithId[] = internalFacilities.map(
    (f) => ({
      id: f.id,
      name: f.name,
    }),
  );

  const availableExternalOrgs: FacilityWithId[] = externalFacilities.map(
    (f) => ({
      id: f.id,
      name: f.name,
    }),
  );

  return (
    <>
      <div className="superadmin-container">
        <h1>Members</h1>
        {loadError && (
          <div className="superadmin-error-banner">{loadError}</div>
        )}
        {isLoading ? (
          <LoaderMessage message={"Fetching members data..."} />
        ) : tableData.length > 0 ? (
          <CustomTable
            tableHeaders={SuperAdminTableHeaderColumns}
            tableData={filteredTableData}
            count={filteredTableData.length}
            itemsPerPage={12}
            pagesPerView={5}
            tableName="superadmin-members"
            searchingText={handleSearch}
            placeholder="Search by name or email"
            onChangeEmit={(toggleData: any) => {
              const rowData = filteredTableData.find(
                (row: any) =>
                  row[SuperAdminTableColumnKeys.Active]?.id === toggleData.name,
              );
              if (rowData) {
                const memberId =
                  rowData[SuperAdminTableColumnKeys.Active]?.memberId;
                if (memberId) {
                  handleToggleActive(memberId, toggleData.value);
                }
              }
            }}
          />
        ) : (
          <div className="superadmin-no-data">No members found.</div>
        )}
      </div>

      <UserAccessSidePanel
        isOpen={isAccessPanelOpen}
        onClose={handleAccessPanelClose}
        onSave={handleAccessPanelSave}
        memberName={selectedMember?.name || ""}
        memberEmail={selectedMember?.email || ""}
        memberId={selectedMember?.id || ""}
        currentRole={selectedMember?.role || "Member"}
        internalOrgIds={selectedMember?.internalOrgIds || []}
        externalOrgIds={selectedMember?.externalOrgIds || []}
        availableInternalOrgs={availableInternalOrgs}
        availableExternalOrgs={availableExternalOrgs}
        isSaving={isSaving}
      />

      <ResetTwoFactorModal
        isOpen={memberToReset !== null}
        memberName={memberToReset?.name || memberToReset?.email}
        onConfirm={handleResetTwoFactorConfirm}
        onClose={handleResetTwoFactorClose}
      />

      <DeleteUserModal
        isOpen={memberToDelete !== null}
        memberId={memberToDelete?.id || ""}
        memberName={memberToDelete?.name || memberToDelete?.email}
        onDelete={handleDeleteMemberConfirm}
        onClose={handleDeleteMemberClose}
      />
    </>
  );
};

export default SuperAdminPage;
